import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { useStytchSession, useStytchUser } from "@stytch/react";
import { Outlet, useLocation } from "react-router-dom";
import DesktopNav from "./navigation/DesktopNav";
import MobileNav from "./navigation/MobileNav";
import Loading from "@/components/Loading";
import EmailVerification from "@/components/EmailVerification";
import ScrollToTop from "@/components/scroll/ScrololToTop";
import useInformizely from "@/hooks/useInformizely";
import useUser from "@/lib/queries/user/useUser";
import ProgressChecklist from "@/components/ProgressChecklist";
import NewLandingPage from "@/pages/NewLandingPage";
import { cn } from "@/lib/utils";
import MarketingLayout from "./MarketingLayout";

function AppLayout({ children }: { children?: React.ReactNode }) {
  const { session, fromCache: sessionFromCache } = useStytchSession();
  const { user, fromCache: userFromCache } = useStytchUser();
  const { data: userData } = useUser({
    enabled: !!session && !sessionFromCache,
  });
  const isDesktop = useMediaQuery("(min-width: 768px)");
  useInformizely({
    enabled: userData?.settings?.pilot,
    pageId: "settings",
  });
  // ALWAYS call routing & other hooks before any return (Rules of Hooks)
  const { pathname } = useLocation();
  const isToolRoute =
    /communication|lesson|resource|research|customiser|generator|lab/i.test(
      pathname
    );

  // Compute container classes before any early returns
  const containerClass = cn(
    "flex flex-col w-full overflow-x-hidden md:pl-[70px]",
    isDesktop ? "md:h-[100dvh] md:min-h-0 md:overflow-hidden" : "min-h-dvh"
  );

  // Make inner content scrollable when outer container is fixed-height on desktop
  const contentClass = cn(
    "flex flex-col flex-1 w-full min-h-0",
    isDesktop ? "md:overflow-y-auto" : undefined
  );

  // Early return blocks AFTER all hooks
  if (session) {
    const authFactors = session.authentication_factors || [];
    if (authFactors.length === 1) {
      if (authFactors[0].type === "password" && !user?.emails[0].verified) {
        if (!user?.emails[0].email) {
          return <Loading />;
        }
        return (
          <>
            {!isDesktop && <MobileNav />}
            {isDesktop && <DesktopNav />}
            {children}
            <EmailVerification />
          </>
        );
      }
    }
  }

  if (userFromCache && sessionFromCache) {
    return <Loading />;
  }

  // Public root landing: show new marketing page when not authenticated and at root path
  if (!session && pathname === "/") {
    return (
      <MarketingLayout>
        <NewLandingPage />
      </MarketingLayout>
    );
  }

  return (
    <>
      {isDesktop && <DesktopNav />}
      <div
        className={containerClass}
        data-tool-layout={
          isDesktop ? (isToolRoute ? "desktop-tool" : "desktop") : "standard"
        }
      >
        <ScrollToTop />
        {!isDesktop && <MobileNav />}
        {children}
        <div className={contentClass}>
          <Outlet />
        </div>
        <ProgressChecklist />
      </div>
    </>
  );
}

export default AppLayout;
